import React, { useReducer, useEffect, useState, useRef } from 'react';
import { injectIntl, FormattedMessage, FormattedPlural } from 'react-intl';
import Loadable from 'react-loadable';
import { useCSS } from '../../lib/hooks';

import { useDrag } from 'react-dnd';
import { useTouch } from '../../lib/hooks';
import { i18nIntl, getMediaProps, getPromotionMainColor, affect_color_filter, isNotEmpty, addHttpsIfMissing, isDesktop } from '../../lib/ep.js';
import { stage_categories } from '../../data/bootstrap_stages.js';

import ShareEntry from './layers/ShareEntry';

import GalleryCoverEmbed from './GalleryCoverEmbed';
import PhotoEmbed from './PhotoEmbed';
import VideoEmbed from './VideoEmbed';
import TextEmbed from './TextEmbed';
import LinkEmbed from './LinkEmbed';
import UserAvatar from './UserAvatar';
import MyNetsButtons from './MyNetsButtons';

import ActionButton, { SquaredButton } from '../builder/ActionButton';
import ActionLink from '../builder/ActionLink';
import { localizeFields } from '../builder/DynamicFields';
import ButtonCollection from '../builder/ButtonCollection';
import CountryFlag from '../builder/CountryFlag';
import RibbonCollection from '../ui_items/RibbonCollection.js';
import { defaults } from '../promotion/bootstrap.js';
import { getEmptyImage } from 'react-dnd-html5-backend';

const max_media_per_gallery = 5;
const ESCAPE_KEY = 27;
const ARROW_L = 37;
const ARROW_R = 39;

const PdfEmbed = Loadable({
	loader:() => import('./PdfEmbed'),
	loading:() => <div>Loading...</div>,
});

const getColorGradient = (main_color, pos) => {
	const modifiers = [0, -0.45, 0.3, -0.15, 0.2, -0.6, 0.5];
	return affect_color_filter(modifiers[pos], main_color);
};

export const getVisualName = (stage, participation, display_name_as_nickname = false) => {
	const voting_public_name_display = stage?.voting_public_name_display ?? '1';

	let hide_username_and_date = stage?.gallery_hide_username ?? false;	// keep manteinance with old field to show/hide name
	if(stage.voting_public_name_display != undefined) {
		hide_username_and_date = (voting_public_name_display == '0') ? true : false;
	}

	if(hide_username_and_date) return false;

	let participation_name = participation.name;
	
	if(voting_public_name_display == '1') return (participation_name != "") ? participation_name : false;

	const custom_data = participation?.quiz_entry?.form ?? false;

	if(custom_data !== false && custom_data[voting_public_name_display] != undefined && custom_data[voting_public_name_display] != '') {
		return custom_data[voting_public_name_display];
	}

	return (participation_name != "") ? participation_name : false;
};

export const GalleryTags = ({ user, promotion, ...props }) => {
	const user_selected_tags = user?.Participation?.quiz_entry?.tags ?? [];
	const promotion_tags = localizeFields(promotion?.available_media_tags ?? [],props.locale);

	const tags = [];
	if(promotion_tags.length > 0 && user_selected_tags.length > 0) {
		user_selected_tags.map((tag) => {
			const _tag = promotion_tags.find((t) => (tag == t.id));
			if(_tag == undefined) return;
			tags.push(_tag);
		});
	}

	if(tags.length == 0) return null;

	const classes = ['user_tags'];
	if(isNotEmpty(props, 'classes')) classes.push(props.classes);

	return (
		<div class={classes.join(" ")} style={props.style}>
			<RibbonCollection
				items={tags}
			/>
		</div>
	)
};

const UserCountry = ({ country_id, ...props }) => {
	if(country_id == undefined || country_id == '') return null;

	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const classes = ['user_country'];
	if(props?.with_avatar ?? false) classes.push('with_avatar');
	if(props?.with_short_description ?? false) classes.push('with_short_description');

	let display_country_name = props?.display_country_name ?? true;
	if(window.countries == undefined) display_country_name = false;

	let country = false;
	if(window.countries != undefined) {
		if(Array.isArray(window.countries)) country = window.countries.find((c) => (c.k == country_id.toLowerCase())).v;
		if(country === false) country = window.countries[country_id.toUpperCase()];
	}

	return (
		<div class={classes.join(" ")}>
			<CountryFlag
				country_id={country_id}
				height={false}
			/>

			{(country !== false && display_country_name) && (
				<span class="country_name gui_cutTextOnOverflow">{i18n(country)}</span>
			)}
		</div>
	)
};

const socialNetworks = (participation) => {
	const user_networks = [];

	Object.keys(participation.extra?.nets ?? {}).map((net) => {
		if(net == 'loc') return;

		user_networks.push({
			net,
			target:participation.extra.nets[net]
		});
	});

	return user_networks;
};

const getSocialMediaURL = (media, current_media, intl) => {
	let media_social_link = false;
	let media_social_link_label = false;
	let media_social_icon = null;
	const this_media = media?.[current_media] ?? {};
	if(this_media.xxss != undefined){
		media_social_link = addHttpsIfMissing(this_media.url);
		media_social_link_label = intl.formatMessage({id:'View in {network}'},{network:this_media.xxss});
		media_social_icon = `ep-${this_media.xxss}`;
	}
	return {
		media_social_link,
		media_social_link_label,
		media_social_icon
	};
};

const UserPostSocialLink = ({ media, ...props }) => {
	const { media_social_link, media_social_link_label , media_social_icon} = getSocialMediaURL(media, props.current_media, props.intl);

	if(media_social_link === false) return null;

	return (
		<div class="user_links">
			<ActionLink
				_class={{ other:'short_description view_in_social_network' }}
				_href={media_social_link}
				_target='_blank'
				icon={media_social_icon}
			>{media_social_link_label}</ActionLink>
		</div>
	)
};

const UserLinks = ({ participation, ...props }) => {
	const user_networks = socialNetworks(participation);

	const external_link = (isNotEmpty(participation.extra, 'external_link')) ? participation.extra.external_link : false;
	const external_link_label = participation.extra?.external_link_label ?? "Visit my site";
	if(user_networks.length == 0 && external_link === false) return null;

	return (
		<div class="user_links">
			{(external_link !== false) && (
				<ActionLink
					_class={{ other:'short_description' }}
					_href={addHttpsIfMissing(external_link)}
					_target='_blank'
				>{external_link_label}</ActionLink>
			)}

			{(user_networks.length > 0) && (
				<MyNetsButtons
					networks={user_networks}
					include_path={true}
					type="success"
				/>
			)}
		</div>
	)
};

const TriangledIconBg = ({ direction, type, ...props }) => {
	if(type == undefined) type = 'detailed';
	const icon_size = 50;

	if(direction == undefined) direction = 1;
	const text_direction = (window.is_rtl != undefined && window.is_rtl) ? -1 : 1;
	const final_direction = text_direction * direction;

	const g_transform = (final_direction > 0) ? null : `translate(${icon_size} 0) scale(${final_direction} 1)`;

	if(type == 'simple') {
		return (
			<svg class="triangled_icon_bg" width={`${icon_size}px`} height={`${icon_size}px`} viewBox={`0 0 ${icon_size} ${icon_size}`}>
				<g transform={g_transform}>
					<polygon class="bg" points="0,0.14 0,50 50,0"/>
				</g>
			</svg>
		)
	}

	return (
		<svg class="triangled_icon_bg" width={`${icon_size}px`} height={`${icon_size}px`} viewBox={`0 0 ${icon_size} ${icon_size}`}>
			<g transform={g_transform}>
				<polygon class="shadow" fill="rgba(0, 0, 0, 0.75)" points="2,49 49,2 50,2 50,0 23,23 0,50 2,50"/>
				<path class="bg" d="M50,0c0,0-11.41,0.5-25,0.5c-6.25,0-24.5,0-24.5,0c0,0,0,18.25,0,24.5C0.5,38.59,0,50,0,50s18.25-19.25,24.5-25.5S50,0,50,0z"/>

				<path class="darken" fill="rgba(0, 0, 0, 0.1)" d="M0,50c0,0,18.25-19.25,24.5-25.5l-24-24c0,0,0,18.25,0,24.5C0.5,38.59,0,50,0,50z"/>
				<path class="lighten" fill="rgba(255, 255, 255, 0.1)" d="M50,0c0,0-19.25,18.25-25.5,24.5l-24-24c0,0,18.25,0,24.5,0C38.59,0.5,50,0,50,0z"/>
			</g>
		</svg>
	)
};

const DesktopGallery = ({ current_media, is_in_cart, custom_cover_color, closeEntryAsLayer, is_selected, gallery_count, intl, vote_info, main_color, media, openEntryAsLayer, goNextMedia, goPrevMedia, participation, ...props }) => {
	if(gallery_count === 0) return null;

	const i18n = (m, c) => i18nIntl(m, intl, c);

	const hide_entries_nav = (is_selected && !isDesktop()) ? true : false;
	const has_more = (media.length > max_media_per_gallery) ? true : false;
	const first_media = media[0];
	const first_media_type = first_media?.type ?? 'text';
	const first_media_orientation = (first_media_type == 'text' || first_media_type == 'url' || parseInt(first_media.width) >= parseInt(first_media.height)) ? 'l' : 'p';
	const is_voted = (vote_info != undefined && vote_info.already_voted != undefined && vote_info.already_voted) ? true : false;
	const is_in_ranking = props?.is_in_ranking ?? false;

	const display_voted_icon = props?.display_voted_icon ?? true;

	let component_props = {};
	let Component = PhotoEmbed;
	let type_media_selected = '';
	if(is_selected) {
		component_props = getMediaProps(media[current_media]);

		const _base_color = (custom_cover_color !== false) ? custom_cover_color : ((media[current_media].main_color != undefined) ? media[current_media].main_color : main_color);
		component_props.main_color = getColorGradient(_base_color, current_media);

		type_media_selected = (media[current_media].type != undefined) ? media[current_media].type : 'image';

		switch(type_media_selected) {
			case 'pdf':
				Component = PdfEmbed;
				break;

			case 'text':
				Component = TextEmbed;
				component_props.text = component_props.file.text;
				break;

			case 'url':
				Component = LinkEmbed;
				component_props.url = component_props.file.url;
				component_props.title = component_props.file.title;
				component_props.description = component_props.file.description;
				break;

			case 'video':
			case 'vimeo':
			case 'youtube':
				Component = VideoEmbed;
				component_props.controls = '1';
				component_props.display_place = 'multimedia';
				component_props.key = `video${current_media}`;
				break;

			case 'cover':
				Component = GalleryCoverEmbed;
				const displayed_named = getVisualName(props.stage, participation);

				if(displayed_named !== false) {
					component_props.author = displayed_named;
					component_props.date = participation.created;
				}
				break;

			case 'image':
			default:
				const { media_social_link } = getSocialMediaURL(media, current_media, intl);
				component_props.full_link = media_social_link;
				break;
		}
	}
	
	const gallery_proportion = props.stage?.gallery_proportion ?? '15x12';

	const display_media_type_icon = (is_in_ranking || is_in_cart) ? false : true;

	return (
		<div class="entry_gallery_container">
			{(is_selected && type_media_selected == 'cover') && (
				<Component
					type="big"
					{...component_props}
				/>
			)}
	
			{(display_voted_icon && is_voted && is_selected) && (
				<span class="voted_mark on_big redIcon ep-heart">
					<TriangledIconBg direction={1} />
				</span>
			)}

			{(is_selected) && (
				<a class="close_mark on_big knk-off original_color_item close_layer_btn" onClick={closeEntryAsLayer}>
					<TriangledIconBg direction={-1} type="simple" />
				</a>
			)}

			<div class="entry_gallery">
				{(!is_selected) && (
					<img src={`${window.cdn_url}img/blank_${gallery_proportion}.png`} width="100%" alt="" />
				)}

				{(is_selected) && (
					<div class={'big selected' + ((hide_entries_nav || gallery_count == 1) ? ' without_nav' : '')}>
						{(type_media_selected != 'cover') && (
							<Component
								type="big"
								{...component_props}
								autoplay={false}
							/>
						)}
					</div>
				)}

				{(!is_in_cart && hide_entries_nav) && (
					<ul class="entry_minimal_nav">
						{media.map((element, m) => {
							let nav_classes = ['original_color_item'];
							if(is_selected && current_media == m) nav_classes.push('selected')
							return (
								<li key={`nav_${m}`} class={nav_classes.join(" ")} />
							)
						})}
					</ul>
				)}

				{(!hide_entries_nav && !(gallery_count == 1 && is_selected)) && (
					<ul class={`gui_media_gallery is_${first_media_orientation} has_${gallery_count}${(has_more) ? ' has_more' : ''}`}>
						{(is_selected && gallery_count > 1) && (
							<li class="nav">
								<ActionButton
									color='transparent'
									icon='knk-arrow-l-big'
									_class={{ other:'entries_navigation prev_btn' }}
									onClick={goPrevMedia}
								/>
							</li>
						)}

						{media.map((element, m) => {
							const type_media = element.type != undefined ? element.type : 'image';
							let li_classes = [];
							let _base_color_el = (custom_cover_color !== false) ? custom_cover_color : ((element.main_color != undefined) ? element.main_color : main_color);

							switch(type_media) {
								case 'video':
								case 'vimeo':
									//element.full_path = `https://i.vimeocdn.com/video/${element.pictures_id}`;
									if(display_media_type_icon) li_classes.push('original_color_item with_media_icon ep-video');
									break;

								case 'youtube':
									if(!is_selected) element.full_path = element.thumbnail;
									if(display_media_type_icon) li_classes.push('original_color_item with_media_icon ep-video');
									break;

								case 'cover':
									element.main_color = getColorGradient(_base_color_el, m);

									if(is_selected) element.title = i18n("Cover");
									break;

								case 'audio':
									if(display_media_type_icon) li_classes.push('original_color_item with_media_icon ep-audio');
									break;

								case 'pdf':
									if(display_media_type_icon) li_classes.push('original_color_item with_media_icon ep-pdf');
									element.filename = element.cover_name;
									break;

								case 'text':
									if(display_media_type_icon) li_classes.push('original_color_item');
									element.main_color = getColorGradient(_base_color_el, m);
									if(is_selected) element.title = i18n("Text");
									break;

								case 'url':
									if(display_media_type_icon) li_classes.push('original_color_item');
									element.main_color = getColorGradient(_base_color_el, m);
									break;

								case 'image':
									if(display_media_type_icon && element.xxss != undefined) {
										li_classes.push('original_color_item with_media_icon gui_small_icon');

										switch(element.xxss) {
											case 'instagram':
												li_classes.push('ep-instagram is_logo');
												break;

											case 'tiktok':
												li_classes.push('ep-tik-tok is_logo');
												break;
										}
									}
								default:
									break;
							}

							const has_more_number = (has_more && !is_selected && m == max_media_per_gallery - 1) ? (media.length - max_media_per_gallery + 1) : false;
							if(!is_selected && m > max_media_per_gallery - 1) return null;

							if(is_selected && current_media == m) li_classes.push('selected');

							return (
								<li key={m} class={li_classes.join(" ")} onClick={() => openEntryAsLayer(participation.id, m)}>
									{(type_media == 'cover') && (
										<GalleryCoverEmbed
											{...element}
											type={((is_selected) ? 'thumb' : 'gallery')}
										/>
									)}

									{(type_media == 'text') && (
										<TextEmbed
											{...element}
											type={((is_selected) ? 'thumb' : 'gallery')}
										/>
									)}

									{(type_media == 'url') && (
										<LinkEmbed
											{...element}
											type={((is_selected) ? 'thumb' : 'gallery')}
										/>
									)}

									{(type_media != 'cover' && type_media != 'text' && type_media != 'url') && (
										<PhotoEmbed
											file={element}
											type={((is_selected) ? 'thumb' : 'gallery')}
											type_media={type_media}
										/>
									)}

									{(has_more_number) && (
										<span class="more_imgs">
											<span class="original_color_item">+{has_more_number}</span>
										</span>
									)}
								</li>
							)
						})}

						{(is_selected && gallery_count > 1) && (
							<li class="nav">
								<ActionButton
									color='transparent'
									icon='knk-arrow-r-big'
									_class={{ other:'entries_navigation next_btn' }}
									onClick={goNextMedia}
								/>
							</li>
						)}
					</ul>
				)}
			</div>
		</div>
	)
};

const MobileGallery = ({ current_media, custom_cover_color, gallery_count, main_color, media, ...props }) => {
	if(gallery_count === 0) return null;
	return (
		<ul class="entry_gallery_listed">
			{media.map((element, m) => {
				let component_props = getMediaProps(element);

				//component_props.main_color = (custom_cover_color !== false) ? custom_cover_color : ((media[current_media].main_color != undefined) ? media[current_media].main_color : main_color);

				const _base_color = (custom_cover_color !== false) ? custom_cover_color : ((media[current_media].main_color != undefined) ? media[current_media].main_color : main_color);
		
				let Component = PhotoEmbed;
				const type_media = element?.type ?? 'image';

				switch(type_media) {
					case 'video':
					case 'vimeo':
					case 'youtube':
						Component = VideoEmbed;
						component_props.controls = '1';
						component_props.display_place = 'multimedia';
						break;

					case 'text':
						component_props.device = 'mobile';
						component_props.text = component_props.file.text;
						component_props.main_color = getColorGradient(_base_color, m);
						Component = TextEmbed;
						break;

					case 'url':
						component_props.device = 'mobile';
						component_props.url = component_props.file.url;
						component_props.title = component_props.file.title;
						component_props.description = component_props.file.description;
						component_props.main_color = getColorGradient(_base_color, m);
						Component = LinkEmbed;
						break;

					case 'pdf':
						component_props.device = 'mobile';
						Component = PdfEmbed;
						break;

					case 'cover':
						component_props.main_color = getColorGradient(_base_color, m);
						Component = GalleryCoverEmbed;
						break;
				}
				return (
					<li key={m}>
						<Component
							{...component_props}
							type="big"
						/>

						{(isNotEmpty(element, 'caption')) && (
							<div class="user_comment" dangerouslySetInnerHTML={{ __html:element.caption.replace(/\n/g, '<br />') }} />
						)}
					</li>
				)
			})}
		</ul>
	)
};

const GalleryFooter = ({ is_votable, is_editable, is_selected, is_picked_to_vote, has_available_cart_galleries_to_vote, intl, voteForEntry, user, voting_enable_ranking, vote_info, main_color, promotion, stage, participation, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, intl, c);

	const is_builder = props?.is_builder ?? false;
	const has_more_participations = props?.has_more_participations ?? true;
	const is_closed = props?.is_closed ?? false;
	const is_pending = props?.is_pending ?? false;
	const fake_frontend = props?.fake_frontend ?? false;
	const vote_enabled = (has_more_participations && !is_closed && !is_pending) ? true : false;

	const vote_cta_label = i18n("vote");
	const pending_to_vote_label = i18n("voted");

	const vote_count = vote_info?.points ?? 0;
	const can_pick_to_vote = (!is_picked_to_vote && has_available_cart_galleries_to_vote) ? true : false;

	const vote_btn_classes = ['vote_button'];

	const has_share_enabled = (promotion.config.share != undefined && promotion.config.share.status != undefined && promotion.config.share.status == '0') ? false : true;

	const hide_votes_to_visitors = promotion?.galleries_hide_votes_to_visitors ?? false;

	let display_vote_count = stage?.voting_enabled ?? false;
	if(hide_votes_to_visitors && !(props?.is_logged ?? false)) display_vote_count = false;

	let has_contents = false;
	if(voting_enable_ranking && !is_selected && display_vote_count) has_contents = true;
	if(is_votable) has_contents = true;
	if(is_editable && !is_selected) has_contents = true;
	if(is_selected && has_share_enabled && !fake_frontend) has_contents = true;

	let hide_this = true;
	if(voting_enable_ranking && is_selected) hide_this = false;
	if(has_contents) hide_this = false;
	if(hide_this) return null;

	const is_voted = (vote_info != undefined && vote_info.already_voted != undefined && vote_info.already_voted) ? true : false;

	const footer_classes = ['participation_footer', 'original_color_item'];
	if(is_voted || is_closed || is_pending) footer_classes.push('is_voted');
	if(!can_pick_to_vote) footer_classes.push('is_pending_vote');
	if(!has_more_participations) footer_classes.push('is_finished');

	const voting_type = stage?.voting_type ?? 'simple';
	const vote_units_sing = (voting_type == 'simple') ? "{num} vote" : "{num} point";
	const vote_units_plur = (voting_type == 'simple') ? "{num} votes" : "{num} points";

	const disable_edit_button = is_editable ? (stage.type == stage_categories.ID_PHOTO_FUN.id ? true : false) : false;
	
	return (
		<div class="footer_container" style={(is_selected) ? {width:'100%'} : null}>
			{(voting_enable_ranking && is_selected) && (
				<span class="vote_label in_top in_detail">
					{(display_vote_count) && (
						<FormattedPlural
							value={vote_count}
							one={<FormattedMessage id={vote_units_sing} values={{ num:vote_count }} />}
							other={<FormattedMessage id={vote_units_plur} values={{ num:vote_count }} />}
						/>
					)}
				</span>
			)}

			{(has_contents) && (
				<div class={footer_classes.join(" ")}>
					<div class="participation_actions">
						{(voting_enable_ranking && !is_selected && display_vote_count) && (
							<span class="vote_label in_top">
								<FormattedPlural
									value={vote_count}
									one={<FormattedMessage id={vote_units_sing} values={{ num:vote_count }} />}
									other={<FormattedMessage id={vote_units_plur} values={{ num:vote_count }} />}
								/>
							</span>
						)}

						{(is_votable) && (
							<div class="flex_button in_top adaptable_button gui_actions">
								<ActionButton
									_fullclass={vote_btn_classes.join(" ")}
									onClick={(!is_picked_to_vote && !is_builder && vote_enabled) ? () => voteForEntry(user, is_voted) : null}
									autotranslate={false}
								>{(!is_picked_to_vote && !is_voted) ? vote_cta_label : pending_to_vote_label}</ActionButton>
							</div>
						)}

						{(is_editable && !is_selected) && (
							<ButtonCollection>
								{(!disable_edit_button) && (
									<ActionButton
										onClick={props.onEdit}
									>Edit</ActionButton>
								)}
								{(props.extra_footer_button)}
							</ButtonCollection>
						)}

						{(is_selected && has_share_enabled && !fake_frontend) && (
							<ShareEntry
								promotion={promotion}
								stage={stage}
								locale={props.locale}
								user={participation}
								type='link'
								sharing_type={(is_votable) ? 'gallery_voteme' : 'gallery_view'}
							/>
						)}
					</div>
				</div>
			)}
		</div>
	)
};

const PublicExtraFields = ({ field_definitions, fields, ...props }) => {
	if(field_definitions == undefined || field_definitions.length == 0) return null;
	if(fields == undefined || Object.values(fields).length == 0) return null;

	return (
		<div
			class="public_extra_fields"
		>
			{field_definitions.map((f) => {
				return (
					<div key={`f_${f.id}`}>
						<strong>{f.title}</strong>: {fields[f.id]}
					</div>
				)
			})}
		</div>
	)
};

const GalleryParticipantInfo = ({ is_selected, in_registered, vote_info, participation, media, current_media, stage, closeEntryAsLayer, ...props }) => {
	const is_voted = (vote_info != undefined && vote_info.already_voted != undefined && vote_info.already_voted) ? true : false;
	const is_listed = (props.is_listed != undefined) ? props.is_listed : false;
	const force_alignment = (window.is_rtl) ? 'mod_align_right' : 'mod_align_left';
	
	const voting_public_name_display = stage?.voting_public_name_display ?? '1';
	let hide_username_and_date = (voting_public_name_display == '0') ? true : false;

	const display_voted_icon = props?.display_voted_icon ?? true;

	// keep manteinance with old field to show/hide name
	if(stage.voting_public_name_display == undefined && (stage?.gallery_hide_username ?? false)) {
		hide_username_and_date = true;
	}

	const display_avatar = (!in_registered) ? (stage?.voting_user_avatar ?? false) : true;
	const display_created = (!in_registered) ? (stage?.voting_user_created ?? false) : true;
	const display_country = (!in_registered) ? (stage?.voting_user_country ?? false) : true;

	let display_this = false;
	if(!hide_username_and_date) display_this = true;
	if(display_avatar) display_this = true;
	if(display_country) display_this = true;
	if(is_selected) display_this = true;

	if(!display_this) return null;

	const fake_short_description = (isNotEmpty(participation.extra, 'fake_short_description')) ? true : false;

	let has_short_description = (isNotEmpty(participation.extra, 'short_description')) ? true : false;
	if(is_selected && fake_short_description && is_listed) has_short_description = false;

	const user_name = getVisualName(stage, participation);
	const user_networks = socialNetworks(participation);

	const external_link = (isNotEmpty(participation.extra, 'external_link')) ? participation.extra.external_link : false;
	const display_user_links = user_networks.length > 0 || external_link !== false ? true : false;

	const extra_fields_resp = participation.public_extra_fields?.values;

	let has_extra_fields = true;
	if(extra_fields_resp == undefined || Object.values(extra_fields_resp).length == 0) {
		has_extra_fields = false;
	}
	const tz = props?.promotion?.timezone ?? defaults.timezone;
	return (
		<div class="participation_info">
			{(!hide_username_and_date || display_avatar || (!is_selected || is_listed)) && (
				<>
					{(display_voted_icon && is_voted && (!is_selected || is_listed)) && (
						<span class="voted_mark redIcon ep-heart" />
					)}

					{(!hide_username_and_date || display_avatar || ((!is_selected || is_listed) && has_short_description)) && (
						<div class="participant_data">
							{(display_avatar) && (
								<UserAvatar
									user={participation}
								/>
							)}

							<div class="main_user_info">
								{(!hide_username_and_date && user_name !== false) && (
									<strong class={`user_name ${force_alignment}${(display_avatar) ? ' with_avatar' : ''}`}>
										{(props.open_full_view != undefined) ? (
											<ActionLink
												onClick={props.open_full_view}
												autotranslate={false}
											>{user_name}</ActionLink>
										) : (
											user_name
										)}
									</strong>
								)}

								{((!is_selected || is_listed) && has_short_description) && (
									<div
										class="short_description"
										dangerouslySetInnerHTML={{
											__html:participation.extra.short_description.replace(/\n/g, '<br />')
										}}
									/>
								)}
							</div>
						</div>
					)}
				</>
			)}

			{(!is_selected && display_country) && (
				<UserCountry
					country_id={(isNotEmpty(participation.country)) ? participation.country : (participation?.country_id ?? null)}
					with_avatar={display_avatar}
					with_short_description={has_short_description}
					intl={props.intl}
				/>
			)}

			{(is_selected && is_listed) && (
				<SquaredButton
					icon='knk-off'
					_class={{ other:'close_layer_btn' }}
					onClick={closeEntryAsLayer}
					aria-label="Close"
				/>
			)}

			{(is_selected && ((display_country || display_created) || (!is_listed && has_short_description) || display_user_links || has_extra_fields)) && (
				<div class={'user_textual_participation' + ((hide_username_and_date && !is_listed) ? ' without_name' : '')}>
					{(display_country || display_created) && (
						<div class="participation_details">
							{(display_country) && (
								<UserCountry
									country_id={(isNotEmpty(participation.country)) ? participation.country : (participation?.country_id ?? null)}
									with_avatar={display_avatar}
									intl={props.intl}
								/>
							)}

							{(display_created) && (
								<div
									class="user_created"
								>{EPGetTimezone(participation.created, tz, 'LL')}</div>
							)}
						</div>
					)}

					{(!is_listed && has_short_description && !(is_selected && fake_short_description)) && (
						<div
							class="short_description"
							dangerouslySetInnerHTML={{ __html:participation.extra.short_description.replace(/\n/g, '<br />') }}
						/>
					)}
					
					{(is_selected && has_extra_fields) && (
						<PublicExtraFields
							field_definitions={localizeFields(participation.public_extra_fields?.fields, props.locale)}
							fields={extra_fields_resp}
						/>
					)}

					{(display_user_links) && (
						<UserLinks
							participation={participation}
							current_media={current_media}
						/>
					)}

					{(!is_listed && isNotEmpty(media[current_media], 'caption')) && (
						<div
							class="user_comment"
							dangerouslySetInnerHTML={{ __html:media[current_media].caption.replace(/\n/g, '<br />') }}
						/>
					)}

					<UserPostSocialLink
						media={media}
						current_media={current_media} // OJO que podem tenir el nom de COVER
						intl={props.intl}
					/>
				</div>
			)}
		</div>
	)
};

const GalleryParticipantInfoOnCard = ({ is_selected, in_registered, vote_info, participation, media, current_media, stage, closeEntryAsLayer, ...props }) => {
	const force_alignment = (window.is_rtl) ? 'mod_align_right' : 'mod_align_left';
	
	const voting_public_name_display = stage?.voting_public_name_display ?? '1';
	let hide_username_and_date = (voting_public_name_display == '0') ? true : false;

	// keep manteinance with old field to show/hide name
	if(stage.voting_public_name_display == undefined && (stage?.gallery_hide_username ?? false)) {
		hide_username_and_date = true;
	}

	const display_avatar = stage?.voting_user_avatar ?? false;
	const display_country = stage?.voting_user_country ?? false;

	let display_this = false;
	if(!hide_username_and_date) display_this = true;
	if(display_avatar) display_this = true;
	if(display_country) display_this = true;
	if(is_selected) display_this = true;

	if(!display_this) return null;

	return (
		<div class="participation_info vote_confirm_card">
			{(display_avatar) && (
				<UserAvatar
					user={participation}
				/>
			)}

			{(!hide_username_and_date) && (
				<strong class={`user_name ${force_alignment}`}>
					{getVisualName(stage, participation)}
				</strong>
			)}

			{(isNotEmpty(participation.extra, 'short_description')) && (
				<div class="short_description" dangerouslySetInnerHTML={{ __html:participation.extra.short_description.replace(/\n/g, '<br />') }} />
			)}

			{(display_country) && (
				<UserCountry
					country_id={(isNotEmpty(participation.country)) ? participation.country : (participation?.country_id ?? null)}
					intl={props.intl}
				/>
			)}
		</div>
	)
};

const PrivateActions = ({ media, ...props }) => {
	const in_myparticipation = props?.in_myparticipation ?? false;
	const image_medias = media.filter((m) => (m.type == 'image'));

	if(!in_myparticipation || image_medias.length != 1) return null;

	const promotion = props?.promotion ?? false;
	const stage = props?.stage ?? false;
	const user = props?.user ?? false;

	const is_votable_gallery = user?.votable_gallery ?? false;
	return (
		<div class="footer_container" style={{ width:'100%' }}>
			<div class="participation_footer is_private original_color_item">
				<div class="participation_actions">
					<ActionLink
						_href={`${image_medias[0].full_path_originals}${image_medias[0].filename}`}
						//icon='knk-download'
						_target='_blank'
						download='my-image.jpg'
					>Download</ActionLink>

					{(promotion && stage && (user?.share_data ?? false)) && (
						<ShareEntry
							promotion={promotion}
							stage={stage}
							user={user}
							sharing_type={(is_votable_gallery) ? 'gallery_voteme' : 'gallery_view'}
							type='link'
							icon={false}
						/>
					)}
				</div>
			</div>
		</div>
	)
};
const ListedGallery = ({ in_registered, is_in_cart, is_selected, album_title, album_description, closeEntryAsLayer, user, ...props }) => {
	const main_color = getPromotionMainColor(props.promotion);
	const is_in_ranking = props?.is_in_ranking ?? false;
	const fake_frontend = props?.fake_frontend ?? false;
	return (
		<div class="gui_single_entry_contents is_listed">
			<div class="gui_gallery_layer_bg" onClick={closeEntryAsLayer} />

			<div class="main_c">
				<div class="gui_entry_info">
					<GalleryParticipantInfo
						is_listed={true}
						in_registered={in_registered}
						is_selected={is_selected}
						closeEntryAsLayer={closeEntryAsLayer}
						{...props}
					/>

					<PrivateActions
						media={props.media}
						user={user?.Participation ?? {}}
						stage={props?.stage ?? {}}
						promotion={props?.promotion ?? {}}
						in_myparticipation={props.in_myparticipation}
						intl={props.intl}
					/>
				</div>

				{(!in_registered || fake_frontend) && (
					<GalleryTags
						user={user}
						promotion={props.promotion}
						style={(is_in_ranking) ? { marginBlockEnd:'calc(1.5vw - 1em)' } : null}
						locale={props.locale}
					/>
				)}

				{((!is_in_ranking && !in_registered) || fake_frontend) && (
					<GalleryFooter
						main_color={main_color}
						user={user}
						is_selected={is_selected}
						{...props}
					/>
				)}

				<div class="c">
					<MobileGallery
						is_in_cart={is_in_cart}
						main_color={main_color}
						{...props}
					/>
				</div>

			</div>
		</div>
	)
};

const ConfirmCardGallery =  ({ in_registered, is_in_cart, is_selected, album_title, album_description, closeEntryAsLayer, user, ...props }) => {
	let main_color = getPromotionMainColor(props.promotion);

	return (
		<div class="gui_single_entry_contents">
			<GalleryParticipantInfoOnCard
				is_selected={is_selected}
				in_registered={in_registered}
				closeEntryAsLayer={closeEntryAsLayer}
				{...props}
			/>

			<div class="main_c">
				{(is_selected) && (
					<img class="landscape_size" src={`${window.cdn_url}img/blank_120x63.png`} width="100%" alt="" />
				)}

				<div class="c">
					<DesktopGallery
						is_selected={is_selected}
						closeEntryAsLayer={closeEntryAsLayer}
						is_in_cart={is_in_cart}
						main_color={main_color}
						{...props}
					/>
				</div>
			</div>
		</div>
	)
};

const CompactGallery = ({ in_registered, is_in_cart, is_selected, album_title, album_description, closeEntryAsLayer, user, ...props }) => {
	const is_in_ranking = props?.is_in_ranking ?? false;
	const is_preview = props?.is_preview ?? false;
	const fake_frontend = props?.fake_frontend ?? false;
	const main_color = getPromotionMainColor(props.promotion);

	return (
		<div class="gui_single_entry_contents">
			{(props.removeFromCart != undefined) && (
				<SquaredButton
					icon='knk-off redIcon whiteButton'
					color={false}
					_class={{other:'remove_vote'}}
					onClick={() => props.removeFromCart(user)}
					aria-label="Remove media"
				/>
			)}

			{(is_selected) && (
				<div class="gui_gallery_layer_bg" onClick={closeEntryAsLayer} />
			)}

			<div class="main_c">
				{(is_selected) && (
					<img class="landscape_size" src={`${window.cdn_url}img/blank_120x63.png`} width="100%" alt="" />
				)}

				<div class="c">
					<DesktopGallery
						is_selected={is_selected}
						closeEntryAsLayer={closeEntryAsLayer}
						is_in_cart={is_in_cart}
						main_color={main_color}
						{...props}
					/>

					{(!is_in_cart && (!is_in_ranking || is_selected) && !is_preview) && (
						<div class="gui_entry_info">
							<GalleryParticipantInfo
								is_selected={is_selected}
								in_registered={in_registered}
								closeEntryAsLayer={closeEntryAsLayer}
								{...props}
							/>

							<PrivateActions
								media={props.media}
								user={user?.Participation ?? {}}
								stage={props?.stage ?? {}}
								promotion={props?.promotion ?? {}}
								in_myparticipation={props.in_myparticipation}
								intl={props.intl}
							/>

							{(!in_registered || fake_frontend) && (
								<GalleryTags
									user={user}
									promotion={props.promotion}
									style={(is_in_ranking) ? { marginBlockEnd:'calc(1.5vw - 1em)' } : null}
									locale={props.locale}
								/>
							)}

							{((!is_in_ranking && !in_registered) || fake_frontend) && (
								<GalleryFooter
									main_color={main_color}
									is_selected={is_selected}
									user={user}
									{...props}
								/>
							)}
						</div>
					)}

					{props.additional_footer}
				</div>
			</div>
		</div>
	)
};

export const OnlyGallery = ({ user, ...props }) => {
	let main_color = getPromotionMainColor(props.promotion);
	const participation = user.Participation;
	let media = (participation.quiz_entry != undefined) ? [...participation.quiz_entry.media] : [];

	let entry_classes = ['single_entry_in_gallery', 'original_color_item', 'is_thumb', 'gui_remove_pointer_events'];
	return (
		<li class={entry_classes.join(" ")}>
			<CompactGallery
				user={user}
				promotion={{
					config_styles:{
						promotion:{
							style:{
								main_color_bg:main_color,
							}
						}
					}
				}}
				is_in_cart={true}
				selected_entry={false}
				stage={{}}
				media={media}
				gallery_count={media.length}
				fake_frontend={props.fake_frontend}
				additional_footer={props.additional_footer}
			/>
		</li>
	)
};

export const DraggableSingleGallery = injectIntl((props) => {
	const [{isDragging}, drag,previewRef] = useDrag(() => ({
		type:'GALLERY',
		collect:(monitor) => ({
			isDragging:monitor.isDragging(),
		}),
		item:props.user,
	}));

	useEffect(() => {
		previewRef(getEmptyImage(), { captureDraggingState: true });
		if(props.onDragStart != undefined) props.onDragStart(isDragging);
	}, [ isDragging,previewRef ]);

	return (
		<SingleGallery
			{...props}
			ref={drag}
			//isDragging={isDragging}
			isDragging={(false) ? isDragging : false}
			draggable={true}
		/>
	)
});

export const fakeShortDescription = (participation) => {
	if(!isNotEmpty(participation, 'extra'))participation.extra = {};
	if(isNotEmpty(participation, 'short_description')) {
		participation.extra.short_description = participation.short_description;
	} else if((participation?.quiz_entry?.media ?? []).length > 0) {
		const first_non_text_medias = participation.quiz_entry.media.filter((m) => (m.type != 'text' && isNotEmpty(m, 'caption')));

		if(participation.extra == undefined) participation.extra = {};

		if(first_non_text_medias && first_non_text_medias.length > 0) {
			participation.extra.short_description = first_non_text_medias[0].caption;

			// to avoid duplication of the caption and the short_description in the detailed view:
			participation.extra.fake_short_description = true;
		}
	}

	return participation;
}

export const SingleGallery = React.forwardRef(({ user, selected_entry, promotion, stage, ...props }, drag) => {
	if(user == false || user == undefined) return null;
	let participation = localizeFields([{
		...user.Participation,
		short_description:user.Participation.extra?.short_description,
		loc:user.Participation.extra?.loc ?? {},
	}], props.locale)[0];

	if(participation.extra == undefined || typeof(participation.extra) != 'object') participation.extra = {};

	participation = fakeShortDescription(participation);

	if(isNotEmpty(participation, 'external_link')) {
		participation.extra.external_link = participation.external_link;
	}
	if(isNotEmpty(participation, 'external_link_label')) {
		participation.extra.external_link_label = participation.external_link_label;
	}
	
	if(participation.quiz_entry)participation.quiz_entry.media = localizeFields(participation?.quiz_entry?.media ?? [], props.locale);
	if(participation.extra?.nets != undefined) {
		participation.extra.nets = localizeFields([participation.extra.nets], props.locale)[0];
	}

	(participation?.quiz_entry?.media ?? []).map((media) => {
		if(media.type == 'youtube') {
			media.full_path = 'https://i.ytimg.com/vi/';
			media.filename = `${media.youtube_id}/hqdefault.jpg`;
			return media;
		}

		if(media.name == undefined) return media;

		media.filename = media.name;

		return media;
	});

	useCSS('comp/multigallery.css');

	const is_votable = props?.is_votable ?? false;
	const has_more_participations = props?.has_more_participations ?? true;
	const is_editable = props?.is_editable ?? false;
	const is_forced = props?.is_forced ?? false;
	const is_in_cart = props?.is_in_cart ?? false;
	const is_preview = props?.is_preview ?? false;
	const in_registered = props?.in_registered ?? false;
	const is_add_builder = props?.is_add_builder ?? false;
	const fake_frontend = props?.fake_frontend ?? false;
	const is_in_ranking = props?.is_in_ranking ?? false;
	const is_vote_confirm_card = props?.is_vote_confirm_card ?? false;
	const is_selected = (!is_in_cart && (selected_entry === true || selected_entry == participation.id)) ? true : false;
	const has_voting_sidebar = props?.has_voting_sidebar ?? false;
	const draggable = props?.draggable ?? false;
	const album_title = (participation && isNotEmpty(participation.quiz_entry, 'title')) ? participation.quiz_entry.title : false;
	const album_description = (participation && isNotEmpty(participation.quiz_entry, 'description')) ? participation.quiz_entry.description : false;

	let has_cover = (album_title !== false) ? true : false;
	if(is_add_builder && (stage?.media_title ?? '') != '') has_cover = true;

	let media = (participation?.quiz_entry?.media) ? [...participation.quiz_entry.media] : [];
	if(has_cover) {
		media = [{
			type:'cover',
			title:album_title,
			description:album_description,
			main_color:null,	// "null" to use promotion "main_color" by default
			width:500,
			height:300,
		}, ...media];
	}

	const [ state, dispatch ] = useReducer((state, action) => {
		switch (action.type) {
			case 'OPEN_ENTRY':
				return { ...state, current_media:action.media_index };

			case 'NEXT_MEDIA':
				if(participation == false) return state;
				let next_index = (state.current_media + 1) % media.length;
				return { ...state, current_media:next_index };

			case 'PREV_MEDIA':
				if(participation == false) return state;
				let prev_index = (state.current_media - 1);
				if(prev_index < 0) prev_index = media.length - 1;
				return { ...state, current_media:prev_index };

			default:
				return state;
		}
	}, {
		current_media:props?.open_media_index ?? 0,
	});

	const allowOpen = useRef((!is_preview) ? true : false);

	const handleKeyDown = (e) => {
		const frontend_container = document.querySelector('.ep_global_contents');
		const ltr_navigation = (frontend_container != undefined && frontend_container.classList.contains('is_rtl')) ? false : true;

		switch(e.keyCode) {
			case ESCAPE_KEY:
				closeEntryAsLayer();
				break;

			case ARROW_L:
				(ltr_navigation) ? goPrevMedia() : goNextMedia();
				break;

			case ARROW_R:
				(ltr_navigation) ? goNextMedia() : goPrevMedia();
				break;
		}
	};

	const calculateThinScreen = () => {
		return (parseInt(window.innerWidth) < 800) ? true : false;
	};
	const [ display_listed, setDisplayListed ] = useState(calculateThinScreen());

	const detectIfThinScreen = () => {
		setDisplayListed(calculateThinScreen());
	};

	useEffect(() => {
		detectIfThinScreen();
		window.addEventListener('resize', detectIfThinScreen);

		return () => {
			window.removeEventListener('resize', detectIfThinScreen);
		}
	}, []);

	useEffect(() => {
		let t = null;

		if(draggable) {
			if(props.isDragging) {
				allowOpen.current = false;
			} else {
				t = setTimeout(() => {
					allowOpen.current = true;
				}, 200);
			}

			return () => {
				clearTimeout(t);
			}
		}
	}, [ props.isDragging ]);

	useEffect(() => {
		if(!is_selected) return () => {
			document.removeEventListener('keydown', handleKeyDown);
		}

		document.addEventListener('keydown', handleKeyDown);

		return () => {
			document.removeEventListener('keydown', handleKeyDown);
		}
	}, [ is_selected ]);

	const gallery_count = Math.min(max_media_per_gallery, media.length);

	const goPrevMedia = () => {
		if(gallery_count > 1) dispatch({ type:'PREV_MEDIA' });
	};

	const goNextMedia = () => {
		if(gallery_count > 1) dispatch({ type:'NEXT_MEDIA' });
	};

	const voteForEntry = (participation, already_voted = false) => {
		if(props.voteForEntry != undefined) props.voteForEntry(participation, already_voted);
	};

	const closeEntryAsLayer = () => {
		if(props.closeEntryAsLayer != undefined) props.closeEntryAsLayer();
	};

	const openEntryAsLayer = (participation_id, media_index) => {
		if(!allowOpen.current) return;
		dispatch({ type:'OPEN_ENTRY', media_index });

		if(props.openDetailLayer != undefined) {
			props.openDetailLayer(participation_id);
		}
	};

	useTouch({
		leftSwipe:goNextMedia,
		rightSwipe:goPrevMedia,
		enabled:(state.current_participation !== false) ? true : false,
	});

	let entry_classes = ['single_entry_in_gallery', 'original_color_item'];
	if(is_selected) {
		entry_classes.push('in_layer_mode');
		if(has_voting_sidebar) entry_classes.push('with_votes');
	} else if(is_in_cart || in_registered || is_in_ranking) {
		//entry_classes.push('is_thumb');
	}
	if(in_registered && !is_preview) entry_classes.push('awesome');
	if(is_forced) entry_classes.push('in_forced_mode');

	const vote_info = user?.StageVote ?? {};
	const is_picked_to_vote = (props.votes_casted && props.votes_casted.includes(vote_info.id)) ? true : false;
	const has_available_cart_galleries_to_vote = (props.votes_casted && (props.votes_casted).filter((m) => m == false).length > 0) ? true : false;
	const voting_enable_ranking = stage?.voting_enable_ranking ?? false;

	let GalleryComponent = CompactGallery;
	if(is_selected && display_listed) GalleryComponent = ListedGallery;
	if(is_vote_confirm_card) GalleryComponent = ConfirmCardGallery;
	const custom_cover_color = (isNotEmpty(participation.quiz_entry, 'color')) ? participation.quiz_entry.color : false;

	return (
		<li
			ref={drag}
			id={props?.item_id ?? null}
			class={entry_classes.join(" ")}
			style={props?.style ?? {}}
			onClick={props.full_on_click}
		>
			<GalleryComponent
				album_description={album_description}
				album_title={album_title}
				closeEntryAsLayer={closeEntryAsLayer}
				custom_cover_color={custom_cover_color}
				current_media={state.current_media}
				gallery_count={gallery_count}
				goNextMedia={goNextMedia}
				goPrevMedia={goPrevMedia}
				has_cover={has_cover}
				in_registered={in_registered}
				fake_frontend={fake_frontend}
				intl={props.intl}
				is_in_cart={is_in_cart}
				is_picked_to_vote={is_picked_to_vote}
				has_available_cart_galleries_to_vote={has_available_cart_galleries_to_vote}
				is_selected={is_selected}
				is_votable={is_votable}
				has_more_participations={has_more_participations}
				is_editable={is_editable}
				is_preview={is_preview}
				media={media}
				openEntryAsLayer={openEntryAsLayer}
				participation={participation}
				promotion={promotion}
				stage={stage}
				user={user}
				vote_info={vote_info}
				voteForEntry={voteForEntry}
				voting_enable_ranking={voting_enable_ranking}
				removeFromCart={props.removeFromCart}
				display_voted_icon={false}
				{...props}
			/>
		</li>
	)
});

export default injectIntl(SingleGallery);